// Glossary + decision-stage buyer pages. Same shape as AUTHORITY_PAGES so
// they render through the shared AuthorityPage template. Glossary entries
// carry a `definedTerm` field that adds a Schema.org DefinedTerm block for
// AI answer engine citations.

export const DISCOVERY_PAGES = {
  // ---------------- GLOSSARY (6) ----------------
  "what-does-a-brand-strategist-do": {
    key: "glossaryBrandStrategist",
    slug: "glossary/what-does-a-brand-strategist-do",
    canonicalPath: "/glossary/what-does-a-brand-strategist-do",
    eyebrow: "Glossary",
    h1: "What Does a Brand Strategist Do?",
    lead:
      "A brand strategist decides how a business will be understood, remembered, and chosen. The output is a set of decisions about audience, positioning, promise, voice, and proof that everything else (name, logo, website, marketing) is then built to serve.",
    definedTerm: {
      name: "Brand strategist",
      description:
        "A brand strategist is the person who defines how a business is understood, remembered, and chosen by making decisions about audience, positioning, promise, voice, and proof before any visual or marketing work begins.",
    },
    sections: [
      {
        h: "What a brand strategist actually decides",
        rows: [
          { k: "Audience", v: "Who the business is for and, just as importantly, who it is not for." },
          { k: "Positioning", v: "The single-sentence promise that separates this business from the alternatives." },
          { k: "Proof", v: "The evidence the business is going to lead with to make that promise believable." },
          { k: "Voice", v: "How the business sounds when it writes, talks, sells, and answers hard questions." },
          { k: "Priorities", v: "Which brand and marketing moves get built first, second, and last." },
        ],
      },
      {
        h: "How this is different from a designer or marketer",
        body: [
          "Designers make the brand look like something. Marketers get the brand in front of people. A brand strategist decides what the business should actually stand for and stand against before either of those jobs can be done well. Without that layer, design becomes decoration and marketing becomes volume.",
        ],
      },
      {
        h: "When a service business needs a brand strategist",
        body: [
          "Most Canadian service businesses need brand strategy at three moments: when starting up, when the business has outgrown its original positioning, and when marketing spend is producing effort without a return. In each case, the fix is not more assets. It is a clearer set of decisions.",
        ],
        links: [
          { label: "Kostaja brand strategy service", to: "/services/brand-strategy" },
          { label: "Brand & Branding collection", to: "/brand-branding" },
        ],
      },
    ],
    faqs: [
      { q: "Is brand strategy the same as marketing strategy?", a: "No. Brand strategy defines the promise, audience, and voice of the business. Marketing strategy decides how, where, and how often that promise gets communicated. Brand strategy is upstream of marketing strategy." },
      { q: "How long does a brand strategy engagement take?", a: "For a small service business, a focused brand strategy engagement usually takes two to six weeks. For a larger business with multiple audiences or service lines, it can take six to twelve weeks." },
      { q: "Can a business build its own brand strategy without hiring a strategist?", a: "Small businesses often can, if the owner is willing to make hard trade-off decisions in writing. The value of hiring a strategist is speed, external perspective, and being pushed to commit to a position instead of hedging." },
    ],
  },

  "what-is-a-brand-system": {
    key: "glossaryBrandSystem",
    slug: "glossary/what-is-a-brand-system",
    canonicalPath: "/glossary/what-is-a-brand-system",
    eyebrow: "Glossary",
    h1: "What Is a Brand System?",
    lead:
      "A brand system is the connected set of decisions and assets that keep a business recognizable, consistent, and easy to trust across every touchpoint. It is the difference between a logo and a brand.",
    definedTerm: {
      name: "Brand system",
      description:
        "A brand system is the connected set of decisions and assets (name, logo, colour, typography, voice, layout rules, applications) that keep a business looking and reading consistent across every touchpoint.",
    },
    sections: [
      {
        h: "What a brand system includes",
        rows: [
          { k: "Verbal identity", v: "Name, tagline, voice, and the language rules that make the business sound like itself in writing." },
          { k: "Visual identity", v: "Logo, colour, typography, and imagery direction that make the business look like itself." },
          { k: "Layout rules", v: "How the elements combine on a card, a webpage, a slide, or a printed sheet." },
          { k: "Applications", v: "Ready-to-use versions for social profiles, business cards, signage, print, decks, and templates." },
          { k: "Guidelines", v: "A short reference document that keeps the brand consistent when someone else designs on it." },
        ],
      },
      {
        h: "Why a logo alone is not a brand system",
        body: [
          "A logo is a single mark. A brand system is what happens when that mark meets a colour, a typeface, a voice, a layout, a website, a social feed, and a printed leave-behind. Without a system, every application is a fresh guess. With a system, the business looks like itself even when different people design different pieces.",
        ],
      },
      {
        h: "Why service businesses need one",
        body: [
          "Service businesses live on trust. Trust builds through consistency. A brand system is the smallest set of rules that lets a business look and read consistent across every place a customer encounters it, without slowing the operator down.",
        ],
        links: [
          { label: "Brand foundation services", to: "/services/brand" },
          { label: "The Brand & Branding collection", to: "/brand-branding" },
        ],
      },
    ],
    faqs: [
      { q: "Do I need a full brand system if I am just starting out?", a: "You need enough of a system to look like the same business on your website, cards, and social profiles. That can be as simple as a working logo, one primary colour, two typefaces, and a short voice note. Kostaja builds tightly scoped starter brand systems for launching businesses." },
      { q: "How is a brand system different from a brand guide?", a: "A brand system is the whole set of decisions and assets. A brand guide is the short document that captures those decisions so other people can use the system correctly." },
      { q: "When does a brand system need to be rebuilt?", a: "When the business has outgrown its original audience, when new service lines make the current system incoherent, or when the visual and verbal parts have drifted apart to the point where the business no longer looks like itself." },
    ],
  },

  "what-is-local-seo": {
    key: "glossaryLocalSeo",
    slug: "glossary/what-is-local-seo",
    canonicalPath: "/glossary/what-is-local-seo",
    eyebrow: "Glossary",
    h1: "What Is Local SEO?",
    lead:
      "Local SEO is the practical work that makes a business easier for nearby customers to find when they search on Google, Google Maps, or a voice assistant. It is a small set of signals that add up to visibility.",
    definedTerm: {
      name: "Local SEO",
      description:
        "Local SEO is the set of on-site, off-site, and Google Business Profile activities that help a business appear when nearby customers search for the services it offers.",
    },
    sections: [
      {
        h: "What local SEO includes",
        rows: [
          { k: "Google Business Profile", v: "A complete, current profile with correct category, services, hours, service area, photos, and answered reviews." },
          { k: "NAP consistency", v: "Business name, address, and phone number matching across the website, Google, Bing, Yelp, and directories." },
          { k: "Service and location pages", v: "One clear page per real service and per real service area." },
          { k: "Reviews", v: "A steady flow of real customer reviews with responses from the business." },
          { k: "Schema markup", v: "Structured data on the site (LocalBusiness, Service, FAQPage) that tells search engines exactly what the business does." },
          { k: "Local citations", v: "Listings in the directories that matter for the industry and city." },
        ],
      },
      {
        h: "How local SEO is different from general SEO",
        body: [
          "General SEO focuses on ranking for topical queries anywhere. Local SEO focuses on ranking for queries that include, or imply, a location. Google uses a different mix of signals for local queries, with Google Business Profile completeness and review quality doing a large share of the work.",
        ],
      },
      {
        h: "Why service businesses need local SEO",
        body: [
          "Most Canadian service business demand starts with a local search: 'plumber near me', 'best dentist in Oakville', 'family lawyer Hamilton'. If the Google Business Profile is incomplete or the website has no service area pages, the business is invisible to buyers who are already ready to call.",
        ],
        links: [
          { label: "GBP for home services", to: "/google-business-profile-for-home-services" },
          { label: "GBP for restaurants", to: "/google-business-profile-for-restaurants" },
          { label: "Local marketing systems", to: "/local-marketing-systems" },
        ],
      },
    ],
    faqs: [
      { q: "How long does local SEO take to work?", a: "For most service businesses, a properly completed Google Business Profile starts producing measurable lift in two to eight weeks. Sustained ranking improvements typically take three to six months, depending on category and competition." },
      { q: "Can I do local SEO myself?", a: "Yes. Completing a Google Business Profile properly, keeping hours current, answering reviews, and adding service pages to your site are all things a disciplined operator can do. Kostaja can audit or build a starter local SEO foundation if you would rather move faster." },
      { q: "Do paid Google ads replace local SEO?", a: "No. Paid ads and local SEO answer different searches with different levels of trust. Paid ads produce clicks now. Local SEO produces trust and free traffic that compounds. Most service businesses need both, in the right proportion." },
    ],
  },

  "what-is-a-brand-audit": {
    key: "glossaryBrandAudit",
    slug: "glossary/what-is-a-brand-audit",
    canonicalPath: "/glossary/what-is-a-brand-audit",
    eyebrow: "Glossary",
    h1: "What Is a Brand Audit?",
    lead:
      "A brand audit is a structured review of how a business is understood, seen, and heard by real customers, compared to how the owner intends it to be understood. The output is a short list of the fixes that will move the business first.",
    definedTerm: {
      name: "Brand audit",
      description:
        "A brand audit is a structured review of a business's brand identity, messaging, website, social presence, and customer touchpoints, resulting in a prioritized list of fixes.",
    },
    sections: [
      {
        h: "What a brand audit reviews",
        rows: [
          { k: "Positioning", v: "Whether the business's promise is clear and different from its competitors." },
          { k: "Visual identity", v: "Whether the logo, colour, typography, and imagery are consistent and current." },
          { k: "Website", v: "Whether the site explains the offer clearly and moves visitors toward action." },
          { k: "Google Business Profile", v: "Whether the profile is complete, current, and answered." },
          { k: "Social presence", v: "Whether the social channels look and read like the same business." },
          { k: "Sales materials", v: "Whether the print, decks, and proposals hold up next to the site." },
          { k: "Reviews and proof", v: "Whether the corroboration layer supports the promise." },
        ],
      },
      {
        h: "What a brand audit produces",
        body: [
          "A short written document that names what is working, what is quietly hurting the business, and what to fix first. It is not a redesign. It is a diagnosis. Most owners walk away with a two-to-four-week action list, not a twelve-month project.",
        ],
      },
      {
        h: "When to do a brand audit",
        body: [
          "Before spending on a redesign. Before scaling marketing spend. Before adding new services. And whenever the marketing feels expensive but the pipeline feels quiet.",
        ],
        links: [
          { label: "Kostaja brand audit", to: "/services/brand-audit" },
          { label: "Website audit", to: "/services/website-audit" },
        ],
      },
    ],
    faqs: [
      { q: "How long does a brand audit take?", a: "For a small service business, one to two weeks. For a business with multiple locations, service lines, or audiences, two to four weeks." },
      { q: "How is a brand audit different from a website audit?", a: "A website audit focuses only on the site. A brand audit looks at the whole public-facing system: brand, website, GBP, social, sales materials, and reviews. Kostaja offers both separately or as a combined brand and website review." },
      { q: "Do I need a brand audit if I already know the brand feels off?", a: "The value of an audit is not the diagnosis alone. It is the prioritized sequence of fixes. Most owners know something is off. Fewer know which fix will move the business first." },
    ],
  },

  "what-is-brand-equity": {
    key: "glossaryBrandEquity",
    slug: "glossary/what-is-brand-equity",
    canonicalPath: "/glossary/what-is-brand-equity",
    eyebrow: "Glossary",
    h1: "What Is Brand Equity?",
    lead:
      "Brand equity is the commercial value a business earns because of what customers think, feel, and expect about it, on top of the product or service itself. It is the reason two businesses selling the same thing can charge very different prices.",
    definedTerm: {
      name: "Brand equity",
      description:
        "Brand equity is the additional commercial value a business earns from the accumulated recognition, trust, and expectations customers hold about it, above and beyond the underlying product or service.",
    },
    sections: [
      {
        h: "What brand equity actually is",
        body: [
          "Brand equity is what lets a business earn more, keep customers longer, get referred more often, and hire more easily than a competitor that sells the same underlying thing. It is not felt in a single transaction. It is felt across many, and it accumulates quietly over time.",
        ],
      },
      {
        h: "What builds brand equity",
        rows: [
          { k: "Consistent quality", v: "Delivering the same thing, at the same standard, every time." },
          { k: "Consistent presentation", v: "Looking and reading like the same business across every touchpoint." },
          { k: "Consistent promise", v: "Standing for the same thing to the same audience over years." },
          { k: "Corroboration", v: "Real customer language and independent proof that back up the promise." },
          { k: "Recognizable identity", v: "A visual and verbal system customers can pick out without a logo." },
        ],
      },
      {
        h: "Why service businesses often under-invest in brand equity",
        body: [
          "Service businesses often optimize for the next sale, not the tenth. Brand equity is built by the tenth. Owners who invest in a consistent brand and public presence, even when the immediate sale is fine, are the ones who can raise prices later without losing customers.",
        ],
        links: [
          { label: "Brand & Branding collection", to: "/brand-branding" },
          { label: "Kostaja brand strategy service", to: "/services/brand-strategy" },
        ],
      },
    ],
    faqs: [
      { q: "How do you measure brand equity?", a: "For most small service businesses, brand equity shows up in three practical measurements: how many customers arrive already trusting the business, how easily the business can raise prices without churn, and how often it gets named without being searched. Formal measurement (surveys, brand tracking) matters more for larger businesses." },
      { q: "Can a small business build brand equity?", a: "Yes, and often faster than large ones, because the entity is narrow and specific. A small service business that keeps its promise consistent for three to five years usually builds more useful equity than a large business chasing every trend." },
      { q: "Is brand equity the same as brand value?", a: "Not exactly. Brand equity is the underlying strength. Brand value is what someone would pay for the brand as an asset. Equity leads. Value follows." },
    ],
  },

  "what-is-a-service-business": {
    key: "glossaryServiceBusiness",
    slug: "glossary/what-is-a-service-business",
    canonicalPath: "/glossary/what-is-a-service-business",
    eyebrow: "Glossary",
    h1: "What Is a Service Business?",
    lead:
      "A service business is one where the customer is paying for expertise, time, judgement, or care rather than for a physical product. Everything else about how it markets, sells, and delivers flows from that basic difference.",
    definedTerm: {
      name: "Service business",
      description:
        "A service business is any business where the primary value delivered to the customer is expertise, labour, judgement, or care rather than a manufactured or resold physical product.",
    },
    sections: [
      {
        h: "Categories of service businesses",
        rows: [
          { k: "Home services", v: "Contractors, HVAC, cleaning, landscaping, and residential trades." },
          { k: "Health and wellness", v: "Clinics, dental, physiotherapy, esthetics, and fitness practices." },
          { k: "Professional services", v: "Lawyers, accountants, consultants, financial advisors, and boutique firms." },
          { k: "Local services", v: "Salons, repair shops, tutoring, and neighbourhood service operators." },
          { k: "Hospitality and food", v: "Restaurants, cafes, bars, event spaces, and hospitality operators." },
          { k: "Real estate and mortgage", v: "Agents, brokers, mortgage professionals, and property services." },
        ],
      },
      {
        h: "Why marketing looks different for service businesses",
        body: [
          "Service businesses cannot show the product on a shelf. Customers cannot pick it up, test it, or return it. Every buying decision runs through trust: does this business seem competent, safe, and worth the price. That is why brand, website, reviews, and consistency matter more than they do for product businesses.",
        ],
      },
      {
        h: "How Kostaja works with service businesses",
        body: [
          "Kostaja builds the public-facing side of Canadian service businesses: brand, website, Google Business Profile, reviews, social presence, sales materials, and AI-supported communication. Every deliverable is priced against the outcome, not against a feature list.",
        ],
        links: [
          { label: "Marketing for Canadian service businesses", to: "/service-business-marketing-canada" },
          { label: "Industries we serve", to: "/industries" },
        ],
      },
    ],
    faqs: [
      { q: "Is a business with both products and services a service business?", a: "It depends on where most of the value and revenue come from. A restaurant is a service business even though it sells food. A dental practice that sells whitening kits is still a service business. Look at where the customer actually experiences the value." },
      { q: "Do service businesses need branding as much as product businesses?", a: "Arguably more. A product buyer can inspect the product before buying. A service buyer has to trust the business before the work happens. Brand is how that trust is earned before the first call." },
      { q: "What is the single largest marketing lever for a small service business?", a: "For most, a complete and current Google Business Profile paired with a website that has one clean service page per thing you sell. Both can be built in eight weeks or less." },
    ],
  },

  // ---------------- DECISION-STAGE (4) ----------------
  "how-to-choose-a-brand-marketing-agency": {
    key: "howToChooseAgency",
    slug: "how-to-choose-a-brand-marketing-agency",
    canonicalPath: "/how-to-choose-a-brand-marketing-agency",
    eyebrow: "Buyer decision",
    h1: "How to Choose a Brand Marketing Agency",
    lead:
      "Choosing a brand marketing agency is a decision about who will speak for your business in public for years. Get it right and the pipeline gets easier. Get it wrong and you pay to rebuild in eighteen months.",
    sections: [
      {
        h: "Start from the outcome, not the deliverable",
        body: [
          "Most owners start by asking 'who builds websites' or 'who does branding'. That leads to a stack of deliverables that may or may not add up to a working business. Start instead with the outcome you need: more of the right customers, higher-value sales conversations, or a public presence that finally matches the work. Then work back to which agency can produce that.",
        ],
      },
      {
        h: "Six criteria that separate serious agencies from the rest",
        rows: [
          { k: "One decision-maker on your file", v: "Confirm who does the strategy, the writing, and the design. Ask if it is the person who joined the sales call." },
          { k: "Written scope before work starts", v: "A serious agency will name deliverables, timeline, price, and what is not included. Vague scope is the biggest source of overspend." },
          { k: "Plain-English case studies", v: "Look for case studies that name the problem, the deliverable, and the reason behind the choices. Not just pretty pictures." },
          { k: "Honest positioning", v: "No guaranteed rankings, no growth-hack language, no fabricated metrics. Trust agencies that admit what they cannot promise." },
          { k: "Connected services", v: "Brand, website, local search, social, print, and AI should work as one system, not as six invoices." },
          { k: "Canadian context", v: "For Canadian service businesses, working with an agency that understands provincial compliance, Canadian audience norms, and Canadian invoicing removes friction." },
        ],
      },
      {
        h: "How to evaluate a proposal",
        body: [
          "Read the scope first. If the scope is vague, everything downstream will be. Look for a written definition of the outcome, the deliverables, the timeline, and what is not included. Ask what happens if the fit turns out to be off. A serious agency will tell you when someone else is a better fit.",
        ],
        links: [
          { label: "Best brand marketing agency in Canada", to: "/best-brand-marketing-agency-canada" },
          { label: "Questions to ask before hiring", to: "/questions-to-ask-before-hiring-a-branding-agency" },
          { label: "Why Kostaja", to: "/why-kostaja" },
        ],
      },
    ],
    faqs: [
      { q: "How much should a Canadian brand marketing agency cost?", a: "Fixed-scope services for a small service business typically fall between one thousand and ten thousand Canadian dollars. Larger connected projects fall between ten thousand and sixty thousand. Anyone quoting outside those bands without a written scope deserves a second look." },
      { q: "Should I hire an agency or a freelancer?", a: "A freelancer is often the right answer for a specific, well-defined deliverable. An agency is often the right answer when the work spans brand, website, local search, and marketing and needs to add up to one system." },
      { q: "How long does it take to know if an agency is the right fit?", a: "One well-run discovery call. If the agency asks better questions than you were expecting, walks you through their process without pressure, and gives you a written scope within a week, that is usually a strong signal." },
      { q: "What if the agency I hire is not delivering?", a: "A serious agency will offer a written scope check-in at key milestones. If it does not, ask for one. And do not let a project drift past its written timeline without a written explanation." },
    ],
  },

  "questions-to-ask-before-hiring-a-branding-agency": {
    key: "questionsToAskAgency",
    slug: "questions-to-ask-before-hiring-a-branding-agency",
    canonicalPath: "/questions-to-ask-before-hiring-a-branding-agency",
    eyebrow: "Buyer decision",
    h1: "Questions to Ask Before Hiring a Branding Agency",
    lead:
      "The right questions in the discovery call save more money than any contract clause. Use these to separate serious agencies from confident ones.",
    sections: [
      {
        h: "Ask about who does the work",
        rows: [
          { k: "Who owns the strategy on my file?", v: "You want a name, not a team. The person applying judgement should be identifiable and reachable." },
          { k: "Who writes the copy?", v: "Copy is the highest-leverage part of brand and website work. Confirm the person doing it, not the platform they use." },
          { k: "Who designs the visual work?", v: "Especially for brand identity. Ask to see recent portfolio pieces from the exact person who will design yours." },
        ],
      },
      {
        h: "Ask about scope, price, and process",
        rows: [
          { k: "What is the written scope?", v: "A one-page document naming deliverables, timeline, price, and what is not included. If the agency cannot produce one, that is the answer." },
          { k: "How are revisions handled?", v: "Number of rounds included, cost of additional rounds, and how disagreements get resolved." },
          { k: "What is your standard timeline for this kind of work?", v: "Ask for a real range, not a promise. Serious agencies talk about timelines with specificity." },
          { k: "How do you handle scope changes?", v: "Any working relationship will hit scope changes. What matters is how they are handled: in writing, with agreed prices, before work continues." },
        ],
      },
      {
        h: "Ask about proof and fit",
        rows: [
          { k: "Can you show recent work for a business like mine?", v: "Not identical, but structurally similar (service business, similar size, similar market)." },
          { k: "Can I talk to a recent client?", v: "A serious agency has clients willing to answer a short call. If none are, ask why." },
          { k: "When are you not the right fit?", v: "The best answer is a specific one. An agency that cannot name situations where another partner would be better does not know its own edges." },
        ],
      },
      {
        h: "Ask about compliance and ownership",
        body: [
          "For regulated industries (legal, financial, medical, dental), ask how the agency handles compliance review, what the disclosure and disclaimer approach is, and who is responsible for final compliance sign-off. For all industries, ask who owns the working files, the source design files, and the final assets after the engagement.",
        ],
        links: [
          { label: "How to choose an agency", to: "/how-to-choose-a-brand-marketing-agency" },
          { label: "Why Kostaja", to: "/why-kostaja" },
        ],
      },
    ],
    faqs: [
      { q: "Is it rude to ask an agency for references?", a: "No. Serious agencies expect it and are set up for it. If asking makes an agency uncomfortable, that is data." },
      { q: "Should I get multiple quotes?", a: "For larger projects (over ten thousand dollars), yes. For smaller fixed-scope work, one shortlist of one to three is usually enough. What matters is the quality of the conversation, not the number of proposals." },
      { q: "What is a warning sign in a discovery call?", a: "Guaranteed outcomes ('we will get you to page one'), pressure to commit before a written scope, and inability to name a specific person who will own your file." },
    ],
  },

  "signs-you-need-to-rebrand": {
    key: "signsToRebrand",
    slug: "signs-you-need-to-rebrand",
    canonicalPath: "/signs-you-need-to-rebrand",
    eyebrow: "Buyer decision",
    h1: "Signs You Need to Rebrand",
    lead:
      "A rebrand is expensive, disruptive, and worth it only when the current brand is quietly costing the business more than the rebrand will. Here are the signals to watch for.",
    sections: [
      {
        h: "Signals from customers",
        rows: [
          { k: "New customers show up confused", v: "They expect one thing, then are surprised by what the business actually does. The brand is describing something else." },
          { k: "You lose comparison shopping", v: "Buyers come in warm, then choose a sharper-looking competitor even though the work is comparable or better." },
          { k: "Existing customers are surprised the business does something", v: "You launched a new service two years ago and long-time customers still do not know it exists." },
          { k: "Referrals stall", v: "Loyal customers stop referring not because they are unhappy, but because they can no longer explain the business easily." },
        ],
      },
      {
        h: "Signals from the business",
        rows: [
          { k: "The name no longer fits", v: "The company outgrew its founding name, or the name traps it in the original service line." },
          { k: "The audience shifted", v: "You now serve a very different customer than the one the brand was designed for." },
          { k: "The team cannot explain the business in one sentence", v: "Different people describe the company differently. That is a positioning problem." },
          { k: "The visuals feel dated", v: "The logo, colour, or typography read as five to ten years old and the market has moved on." },
        ],
      },
      {
        h: "When a refresh is enough",
        body: [
          "If the audience, offer, and proof are still right and only the visual system is dated, a refresh is enough. Refresh keeps the equity of the existing brand and updates its presentation. Rebrand is for when the upstream decisions themselves have changed.",
        ],
        links: [
          { label: "Kostaja brand audit", to: "/services/brand-audit" },
          { label: "What is a brand audit", to: "/glossary/what-is-a-brand-audit" },
          { label: "Rebrand and relaunch", to: "/needs/rebrand-and-relaunch" },
        ],
      },
    ],
    faqs: [
      { q: "How much does a rebrand cost for a small service business?", a: "A tightly scoped rebrand for a small service business usually falls between five thousand and twenty thousand Canadian dollars, depending on the number of applications (website, print, social, signage). Larger businesses with multiple locations or service lines run higher." },
      { q: "How long does a rebrand take?", a: "For a small service business, four to twelve weeks. For a larger business, three to six months. Kostaja provides a written timeline in the scope before work starts." },
      { q: "Should I rebrand and rebuild the website at the same time?", a: "Usually yes. The website is the largest surface the brand lives on. Building them together saves cost, avoids awkward transitional design, and shortens overall timeline." },
    ],
  },

  "signs-your-website-is-costing-you-sales": {
    key: "signsWebsiteCostingSales",
    slug: "signs-your-website-is-costing-you-sales",
    canonicalPath: "/signs-your-website-is-costing-you-sales",
    eyebrow: "Buyer decision",
    h1: "Signs Your Website Is Costing You Sales",
    lead:
      "Most business websites are not obviously broken. They just quietly lose the sale. These are the specific signals that a website is costing more than it is producing.",
    sections: [
      {
        h: "Signals from analytics",
        rows: [
          { k: "Traffic without inquiries", v: "Visits are steady, but form fills, calls, and bookings are not. The site is attracting attention that never converts." },
          { k: "High mobile bounce", v: "Mobile visitors leave within a few seconds. Usually because the site is slow, the offer is not clear above the fold, or the phone number is buried." },
          { k: "Long time on the home page, short time on services", v: "Visitors read the home page then leave. The service pages are not answering the question." },
        ],
      },
      {
        h: "Signals from sales conversations",
        rows: [
          { k: "You keep re-explaining what the business does", v: "First calls open with 'so, help me understand what you do'. The site is not doing its job before the call." },
          { k: "Buyers ask questions the site already answers", v: "Or the site does not answer the top objections that come up in every sales call." },
          { k: "Price feels harder to defend than it should", v: "Buyers hesitate on price when the site does not build enough trust to justify it before the call." },
        ],
      },
      {
        h: "Signals from the site itself",
        rows: [
          { k: "No dedicated service pages", v: "Every service is described in a paragraph on the home page. Buyers cannot find the exact thing they need." },
          { k: "Vague CTAs", v: "'Learn more' or 'contact us' on every page. No specific next step tied to the specific outcome the page just described." },
          { k: "No proof above the fold", v: "The buyer has to scroll to find reviews, real projects, or client names." },
          { k: "The homepage answers 'who we are' before 'what you get'", v: "Visitors do not care who you are until they know what they get. Order matters." },
        ],
      },
      {
        h: "What a working service business website does",
        body: [
          "It names the outcome above the fold. It explains the offer in plain English. It has one dedicated page per real service. It answers the top three buyer objections without being asked. It ends every page on one specific CTA. It loads fast on mobile. It shows real proof.",
        ],
        links: [
          { label: "Website that does not convert", to: "/needs/website-doesnt-convert" },
          { label: "Kostaja website audit", to: "/services/website-audit" },
          { label: "Website design services", to: "/services/websites" },
        ],
      },
    ],
    faqs: [
      { q: "How fast can I fix a website that is costing sales?", a: "For most small service businesses, a targeted set of copy and structure fixes to the top three pages can be shipped in two to four weeks and often produces a measurable lift within thirty days. A full rebuild takes six to twelve weeks." },
      { q: "Do I need to redesign the whole site, or can I fix specific pages?", a: "Start with the pages that carry the most weight: the home page and the top two service pages. If those pages work harder, the rest of the site can be updated in a slower sequence." },
      { q: "Should I invest in ads to fix the drop-off, or fix the site first?", a: "Fix the site first. Ads to a broken site are the fastest way to lose money in marketing. When the site converts, ads become an accelerant instead of a leak." },
    ],
  },
  // ---------------- COMPARISON / vs (3) ----------------
  "kostaja-vs-diy": {
    key: "kostajaVsDiy",
    slug: "kostaja-vs-diy",
    canonicalPath: "/kostaja-vs-diy",
    eyebrow: "Comparison",
    h1: "Kostaja vs DIY: When to Build It Yourself, When to Hire",
    lead:
      "Some parts of a service business brand and website are genuinely fine to build yourself. Others quietly cost more than they save. This is the honest split, written by Kostaja for owners who are trying to decide.",
    sections: [
      {
        h: "What DIY handles well",
        rows: [
          { k: "Draft copy", v: "The first pass at your service page copy, positioning, and about page. Nobody knows the business better than the owner." },
          { k: "Google Business Profile setup", v: "Filling in categories, hours, service areas, and photos. Careful work, not specialized work." },
          { k: "Ongoing profile maintenance", v: "Weekly posts, hour updates, review responses. Perfect for the operator." },
          { k: "Social profile hygiene", v: "Keeping bios current, handles matched, and links live." },
          { k: "Menu, hours, and simple content updates", v: "Once the site is built to be edited without a developer, most content should be owner-managed." },
        ],
      },
      {
        h: "What DIY quietly costs more than it saves",
        rows: [
          { k: "Positioning and voice", v: "The one-sentence promise that makes the right buyer say yes and the wrong buyer self-filter. Owners are too close to see it." },
          { k: "Full logo and brand system", v: "Canva templates produce assets that look like every other Canva template. Investment in identity compounds over years." },
          { k: "Service-page architecture", v: "Deciding which services need their own page, what each page must answer, and how they link to each other. Small errors here bleed traffic for months." },
          { k: "Website conversion structure", v: "Above-the-fold hierarchy, CTA design, and objection-handling flow. Design decisions with revenue attached." },
          { k: "Structured data and schema", v: "JSON-LD for Organization, Service, FAQPage, LocalBusiness, and BreadcrumbList. Machine-readable trust signals for Google and AI." },
        ],
      },
      {
        h: "The honest math",
        body: [
          "A Kostaja brand foundation or website engagement is usually between three thousand and twelve thousand Canadian dollars. If your business is producing more than one hundred thousand a year in revenue, the payback window on that spend is short. If it is producing less, DIY the visible pieces, hire out the strategic layer through a one-off strategy call or audit, and re-evaluate once revenue supports the investment.",
        ],
      },
      {
        h: "A middle path",
        body: [
          "Kostaja often works with owners on the strategic layer only (positioning, voice, priorities, and page architecture) and hands the execution back to the owner. Or the reverse: the owner keeps the strategy, and Kostaja handles only the visual and technical build. Both are scoped work, both are common.",
        ],
        links: [
          { label: "Kostaja strategy call", to: "/services/strategy-call" },
          { label: "Brand audit", to: "/services/brand-audit" },
          { label: "See all services", to: "/services" },
        ],
      },
    ],
    faqs: [
      { q: "When is DIY definitely the right answer?", a: "When the business is under thirty thousand a year in revenue, when the owner has design or writing skills, or when the goal is short-term validation before real investment. Do the work, keep the equity, and hire when the maths change." },
      { q: "When is hiring definitely the right answer?", a: "When the business has been running for two or more years, when marketing spend is being wasted on a foundation that does not convert, or when the owner's time is worth more than the hourly cost of doing it yourself." },
      { q: "Can I DIY the brand and hire out the website?", a: "Yes, and it is a common split. The risk is a mismatched result: DIY brand often reads amateur, and a professional site cannot rescue an amateur brand. Kostaja will say if the split is realistic before scoping the website side." },
      { q: "Will Kostaja review my DIY work before I commit to hiring?", a: "Yes, that is what an audit does. A brand audit or website audit tells you what to keep, what to fix yourself, and what is worth hiring for. Most owners save real money this way." },
    ],
  },

  "kostaja-vs-cheap-logo-mills": {
    key: "kostajaVsLogoMills",
    slug: "kostaja-vs-cheap-logo-mills",
    canonicalPath: "/kostaja-vs-cheap-logo-mills",
    eyebrow: "Comparison",
    h1: "Kostaja vs Cheap Logo Mills",
    lead:
      "A ninety-nine-dollar logo looks like a bargain right up until it costs a customer, a hiring decision, or a listing. This is what cheap logo mills actually deliver, what Kostaja delivers instead, and why the price gap is the whole point.",
    sections: [
      {
        h: "What cheap logo mills actually sell",
        rows: [
          { k: "A mark, not a system", v: "You get a logo file. That is it. No colour system, no typography, no rules for how the mark is used." },
          { k: "Templates and libraries", v: "Most cheap logos are variations of the same handful of shapes. Fine for a first-year hobby, awkward for a business hoping to look real." },
          { k: "No positioning", v: "Nobody asked what you sell, who you sell to, or what makes you different. The logo cannot signal something the process never uncovered." },
          { k: "No ownership clarity", v: "Font licensing is often not included. Source files are often not delivered. You end up unable to modify or scale the identity." },
          { k: "No connection to the business", v: "The mark is not designed against a website, a card, a truck, or a storefront. It floats." },
        ],
      },
      {
        h: "What Kostaja delivers instead",
        rows: [
          { k: "A brand system", v: "Name treatment, mark, colour palette, typography, layout rules, imagery direction, and voice. Everything that keeps a business looking like itself." },
          { k: "Positioning work first", v: "A working session on who the business is for, what it promises, and what it stands against. The visual work serves that promise." },
          { k: "Applications you can actually use", v: "Business cards, social profiles, signage, print, and web files ready to deploy on day one." },
          { k: "Source files and clear licensing", v: "You own everything. Fonts are licensed correctly. Source files are yours." },
          { k: "Written guidelines", v: "A short reference document that keeps the brand consistent when someone else designs on it later." },
        ],
      },
      {
        h: "The commercial cost of a cheap logo",
        body: [
          "For a service business, the logo is a trust signal before the first call. A cheap logo forces the rest of the marketing to work harder to overcome the first impression. Owners commonly spend more on repeat design work, one-off Canva templates, freelancer patches, and eventual rebranding than a real brand system would have cost in the first place.",
        ],
      },
      {
        h: "When a cheap logo is actually fine",
        body: [
          "For a very short-term test, a side project, a pop-up, or a business that will be rebranded within twelve months, a cheap logo is fine. For a service business that plans to be operating in three years, it is almost never the right call.",
        ],
        links: [
          { label: "Kostaja brand foundation services", to: "/services/brand" },
          { label: "Signs you need to rebrand", to: "/signs-you-need-to-rebrand" },
        ],
      },
    ],
    faqs: [
      { q: "How much should a real brand system cost for a small service business?", a: "A tightly scoped brand foundation for a small service business usually falls between one thousand five hundred and eight thousand Canadian dollars. A full brand system with applications runs higher. Anything under one thousand almost certainly means a mark without a system." },
      { q: "What is wrong with using a logo generator?", a: "Nothing, if the goal is a placeholder. The problem is when a generator logo becomes the permanent identity for a business that plans to charge premium prices. It caps the ceiling on how professional the business can look." },
      { q: "Can Kostaja refresh a cheap logo instead of rebranding?", a: "Sometimes. If the cheap mark is close to workable, Kostaja can build a real brand system around it. More often, the mark itself is holding the business back and a small rebrand is the shorter path." },
    ],
  },

  "kostaja-vs-generic-web-designer": {
    key: "kostajaVsGenericWebDesigner",
    slug: "kostaja-vs-generic-web-designer",
    canonicalPath: "/kostaja-vs-generic-web-designer",
    eyebrow: "Comparison",
    h1: "Kostaja vs a Generic Web Designer",
    lead:
      "A web designer builds you a nice-looking site. Kostaja builds a site that a service business can actually sell from. Both are legitimate. This is the honest difference and when each one fits.",
    sections: [
      {
        h: "What a generic web designer does",
        rows: [
          { k: "Design and build", v: "Layout, visual design, and the technical build in a platform like Wix, Squarespace, Webflow, or WordPress." },
          { k: "Client-provided copy", v: "You supply the words. The designer arranges them attractively." },
          { k: "One person, one skillset", v: "Great at design and build. Not their job to think about positioning, sales conversion, service architecture, or local SEO." },
          { k: "Fixed deliverable", v: "You get a site. What that site produces commercially is a separate question." },
        ],
      },
      {
        h: "What Kostaja does differently",
        rows: [
          { k: "Positioning first", v: "The site is designed around what the business actually sells, to whom, and why. Copy is written to convert, not just to fill space." },
          { k: "Service architecture", v: "Which services deserve their own page, how the pages relate, and how the site scales as the business adds services. Structural decisions, not just cosmetic." },
          { k: "Conversion structure", v: "Above-the-fold hierarchy, CTA design, objection handling, and mobile-first flow decisions built into every page." },
          { k: "Local SEO built in", v: "Structured data, service area pages, and Google Business Profile alignment done as part of the build, not as an upsell after." },
          { k: "One accountable team", v: "Strategy, writing, design, and build owned by the same person from first call to launch." },
        ],
      },
      {
        h: "When a generic web designer is the right fit",
        body: [
          "When the positioning, copy, and service architecture are already worked out and you just need someone to design and build the site well. When you have an in-house marketer running strategy. When budget is tight and the site is a pure design refresh, not a rethink.",
        ],
      },
      {
        h: "When Kostaja is the right fit",
        body: [
          "When the site needs to earn calls, not just look modern. When positioning and service architecture have never been formally decided. When brand, website, local search, and sales materials need to be built as one system. When you want one accountable person from first call through launch instead of coordinating a designer, a copywriter, an SEO, and a project manager.",
        ],
        links: [
          { label: "Website design services", to: "/services/websites" },
          { label: "Signs your website is costing you sales", to: "/signs-your-website-is-costing-you-sales" },
          { label: "Website audit", to: "/services/website-audit" },
        ],
      },
    ],
    faqs: [
      { q: "Is Kostaja more expensive than a generic web designer?", a: "Usually, per project. Because the engagement includes strategy, writing, conversion structure, and local SEO alignment that a generic designer either does not include or subcontracts. Total-cost comparisons that include copywriter, SEO, and strategist fees usually favour Kostaja." },
      { q: "Can Kostaja work with an existing web designer?", a: "Yes. Kostaja can own strategy, copy, and structure and hand the design and build to your designer. Or lead the design and build and hand off maintenance to your team." },
      { q: "What if I only need a small site?", a: "For very small sites (one to three pages), a generic designer is often the better fit on price. Kostaja is worth the difference once the site has more than three service pages, service areas, or a real sales function." },
    ],
  },
};

export const DISCOVERY_PAGE_SLUGS = Object.keys(DISCOVERY_PAGES);
